// Composer Studio — 표 셀 범위 선택 공용 순수 함수.
//
// Shift+클릭 범위 선택과 마우스 드래그 범위 선택(엑셀/HWP식)이 동일한 "anchor~target 직사각
// 범위 내 실제 존재하는 셀" 계산을 공유하도록, EditorPage.handleCanvasClick에 인라인되어 있던
// 로직을 순수 함수로 추출한다(중복 제거 + 단위 테스트 가능).

import type { TableNode, TableCell } from '@safetyhub/shared';
import { expandMergeRect } from '@safetyhub/report-composer';

/** 표 셀 좌표 참조(단일/다중 선택 공통). anchor = selectedCells[0]. */
export type CellRef = { tableId: string; row: number; col: number };

/**
 * 셀 DOM의 `data-composer-cell="${tableId}::${row}::${col}"` 문자열을 CellRef로 파싱한다.
 * tableId 자체에 '::'가 없다는 전제(node id는 `${kind}-${n}` 형식)로 첫/마지막 '::'를 구분자로 본다.
 * 형식이 어긋나거나 row/col이 숫자가 아니면 null.
 */
export function parseCellRef(raw: string): CellRef | null {
  const firstSep = raw.indexOf('::');
  const lastSep = raw.lastIndexOf('::');
  if (firstSep === -1 || lastSep === -1 || firstSep === lastSep) return null;
  const tableId = raw.slice(0, firstSep);
  const row = Number(raw.slice(firstSep + 2, lastSep));
  const col = Number(raw.slice(lastSep + 2));
  if (!Number.isFinite(row) || !Number.isFinite(col)) return null;
  return { tableId, row, col };
}

/**
 * (row,col) 좌표에 시작하는 셀의 점유 사각형 [row..row+rowspan-1] × [col..col+colspan-1]을 구한다.
 * 병합 셀은 시작 좌표에만 존재하므로 그 좌표 셀을 찾아 span으로 영역을 계산한다.
 * 정확히 매칭되는 셀이 없으면(이론상 anchor/target은 실제 클릭 셀이라 존재) 1×1로 방어적 처리.
 */
function cellSpanBox(
  table: TableNode,
  row: number,
  col: number,
): { r0: number; c0: number; r1: number; c1: number } {
  const cell = table.cells.find((c) => c.row === row && c.col === col);
  const rs = cell?.rowspan ?? 1;
  const cs = cell?.colspan ?? 1;
  return { r0: row, c0: col, r1: row + rs - 1, c1: col + cs - 1 };
}

/** 셀의 점유 사각형이 box [R0..R1]×[C0..C1]과 교차하는지(부분 겹침 포함). */
function cellIntersectsBox(
  cell: TableCell,
  R0: number,
  C0: number,
  R1: number,
  C1: number,
): boolean {
  const cr0 = cell.row;
  const cr1 = cell.row + (cell.rowspan ?? 1) - 1;
  const cc0 = cell.col;
  const cc1 = cell.col + (cell.colspan ?? 1) - 1;
  return cr0 <= R1 && cr1 >= R0 && cc0 <= C1 && cc1 >= C0;
}

/**
 * anchor~target 범위에 걸치는 *실제 존재하는* 셀들을 CellRef 배열로 반환한다(span 인지, 엑셀식).
 *
 * 시작 좌표만으로 직사각 범위를 잡으면 병합 셀의 점유 영역을 무시해, 같은 높이/너비 영역의
 * 하위 셀이 선택에서 빠진다. 이를 막기 위해:
 *  1. anchor/target 셀의 점유 영역(rowspan/colspan)을 합쳐 bounding box를 만든다.
 *  2. box 경계에 부분만 걸친 병합 셀이 box 밖으로 삐져나오면 box를 fixpoint로 확장한다
 *     (expandMergeRect 재사용 — mergeCellsRect의 1단계 로직과 동일).
 *  3. 최종 box와 교차하는(점유 영역이 겹치는) 모든 셀을 반환한다.
 *
 * - anchor를 배열 선두로 유지한다(범위에서 anchor를 빼고 맨 앞에 둠) — selectedCells[0]=anchor 불변식.
 * - anchor/target은 같은 표(table.id)에 속한다고 가정한다(호출부에서 sameTable 가드).
 */
export function computeCellRange(table: TableNode, anchor: CellRef, target: CellRef): CellRef[] {
  const aBox = cellSpanBox(table, anchor.row, anchor.col);
  const tBox = cellSpanBox(table, target.row, target.col);
  // 1. anchor/target 점유 영역을 합친 초기 bounding box.
  const r0 = Math.min(aBox.r0, tBox.r0);
  const c0 = Math.min(aBox.c0, tBox.c0);
  const r1 = Math.max(aBox.r1, tBox.r1);
  const c1 = Math.max(aBox.c1, tBox.c1);
  // 2. box에 부분만 걸친 병합 셀을 모두 포함하도록 fixpoint 확장.
  const { r0: R0, c0: C0, r1: R1, c1: C1 } = expandMergeRect(table, r0, c0, r1, c1);
  // 3. 최종 box와 교차하는 모든 셀을 수집(anchor 선두 유지).
  const intersecting = table.cells.filter((cell) => cellIntersectsBox(cell, R0, C0, R1, C1));
  const rest = intersecting
    .filter((cell) => !(cell.row === anchor.row && cell.col === anchor.col))
    .map((cell) => ({ tableId: table.id, row: cell.row, col: cell.col }));
  return [anchor, ...rest];
}
